import { useState, useEffect } from 'react';
import { register as registerRequest, sendVerifyCode } from '../../../../api/auth';
import { USER_TYPE, USER_TYPE_TO_BACKEND } from '../../../../constants/business.constants';

const CODE_TYPE_REGISTER = 1;

/**
 * useRegister - 注册逻辑自定义Hook
 */
export function useRegister(isOpen, onRegisterSuccess) {
  const [userType, setUserType] = useState('enterprise');
  const [benefitTab, setBenefitTab] = useState('enterprise');
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: '',
    position: '',
    company: '',
    phone: '',
    verifyCode: '',
    email: '',
    credentials: '',
    agreeTerms: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [codeCountdown, setCodeCountdown] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // 重置状态
  useEffect(() => {
    if (isOpen) {
      setError('');
      setSuccess('');
      setIsSubmitting(false);
      setIsSendingCode(false);
      setCodeCountdown(0);
    }
  }, [isOpen]);

  // 倒计时
  useEffect(() => {
    if (!codeCountdown) return;
    const timer = setTimeout(() => setCodeCountdown((prev) => Math.max(prev - 1, 0)), 1000);
    return () => clearTimeout(timer);
  }, [codeCountdown]);

  // 权益标签切换
  const handleBenefitTabChange = (nextTab) => {
    setBenefitTab(nextTab);
    if (nextTab === 'enterprise' || nextTab === 'individual') {
      setUserType(nextTab);
    }
  };

  // 表单输入变化
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // 获取验证码
  const handleGetCode = async () => {
    if (!formData.phone) {
      setError('请先填写手机号');
      return;
    }
    try {
      setError('');
      setSuccess('');
      setIsSendingCode(true);
      await sendVerifyCode({ phone: formData.phone, codeType: CODE_TYPE_REGISTER });
      setSuccess('验证码已发送，请注意查收');
      setCodeCountdown(60);
    } catch (sendError) {
      setError(sendError.message || '验证码发送失败，请稍后再试');
    } finally {
      setIsSendingCode(false);
    }
  };

  // 提交注册
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.agreeTerms) {
      setError('请勾选同意用户服务协议及隐私政策');
      return;
    }
    setError('');
    setSuccess('');
    setIsSubmitting(true);
    
    try {
      // 将用户类型字符串转换为后端期望的数字类型
      // 使用常量映射，避免硬编码
      // 1-个人用户, 2-企业用户
      const userTypeValue = USER_TYPE_TO_BACKEND[userType] || USER_TYPE_TO_BACKEND[USER_TYPE.INDIVIDUAL];
      
      const payload = {
        username: formData.username.trim(),
        password: formData.password,
        realName: formData.name.trim(),
        position: formData.position.trim(),
        company: userType === 'enterprise' ? formData.company.trim() : '',
        phone: formData.phone.trim(),
        verifyCode: formData.verifyCode.trim(),
        email: formData.email ? formData.email.trim() : '',
        credentials: formData.credentials ? formData.credentials.trim() : '',
        userType: userTypeValue,  // 发送数字类型：1-个人, 2-企业
      };
      
      const result = await registerRequest(payload);
      setSuccess('注册成功！即将自动登录...');
      
      setTimeout(() => {
        onRegisterSuccess && onRegisterSuccess(result);
      }, 1500);
    } catch (registerError) {
      setError(registerError.message || '注册失败，请检查填写信息');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resolvedBenefitType = benefitTab === 'summary' ? userType : benefitTab;
  const canSendCode = !isSendingCode && codeCountdown === 0;
  const verifyButtonLabel = codeCountdown > 0 ? `重新获取(${codeCountdown}s)` : '获取验证码';

  return {
    userType,
    benefitTab,
    formData,
    isSubmitting,
    error,
    success,
    canSendCode,
    verifyButtonLabel,
    resolvedBenefitType,
    handleBenefitTabChange,
    handleChange,
    handleGetCode,
    handleSubmit,
  };
}

